import { AdminPanelSettings, Menu, MenuOpen } from '@mui/icons-material';
import { Box, styled, useTheme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import type { CSSObject, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { Link } from '~/components/link';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import logo from '~/images/fc-logo-round.svg';
import { useResponsive } from '~/lib/responsive';
import { useSideNav } from '../context';
import { useLandingPath } from '../landing';
import { useNavGroups } from '../navItems';
import { NavGroup, NavItemLink, NetworkButton, TopNav, UserButton } from './';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    zIndex: '1200',
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    width: 0, // hide entirely
    position: 'fixed',
    zIndex: '1200',
  },
});

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export const DrawerHeader = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0),
  maxHeight: '64px',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  ...(!open && {
    justifyContent: 'center',
  }),
}));

const EnvironmentBadge = ({ env }: { env: string }) => {
  if (!env || env === 'production') return null;

  return (
    <Box
      className="environment-badge"
      sx={{
        position: 'absolute',
        top: 8,
        left: -35,
        backgroundColor: 'warning.main',
        color: 'common.white',
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        py: 0.5,
        px: 3.5,
        transform: 'rotate(-45deg)',
        width: 110,
        textAlign: 'center',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }}
    >
      {env}
    </Box>
  );
};

export const SideNav = () => {
  const { t } = useTranslation();
  const { currentUser, env } = useAppContext();

  const { isSmDown } = useResponsive();

  const theme = useTheme();

  const { navOpen, toggleDrawer } = useSideNav();

  const link = useLink();
  const landingPath = useLandingPath();
  const navGroups = useNavGroups();

  const handleClickAway = () => {
    isSmDown && toggleDrawer();
  };

  return (
    <Drawer
      variant="permanent"
      open={navOpen}
      PaperProps={{
        component: 'nav',
        'aria-label': t('common.labels.main_navigation'),
        sx: {
          backgroundColor: 'primary.main',
          border: 0,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DrawerHeader open={navOpen}>
        <EnvironmentBadge env={env} />
        {navOpen && (
          <Link
            sx={{
              flexBasis: '100%',
              marginTop: 0.5,
              // The logo is taller than the header that clips it, so draw the ring well inside
              // its padded image box, around the visible mark.
              '&:focus-visible': { ...theme.mixins.focusRingInset, outlineOffset: -8 },
            }}
            to={link(landingPath)}
          >
            <img alt="FanConnect" src={logo} />
          </Link>
        )}
        <IconButton
          label={
            navOpen ? t('common.actions.close_navigation') : t('common.actions.open_navigation')
          }
          sx={{
            color: 'common.white',
          }}
          onClick={toggleDrawer}
        >
          {navOpen ? <Menu /> : <MenuOpen />}
        </IconButton>
      </DrawerHeader>
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ paddingX: theme.spacing(1) }}>
          <NetworkButton />

          {!isSmDown && currentUser.admin && (
            <NavItemLink
              icon={<AdminPanelSettings />}
              text={t('common.navigation.administrate')}
              href="/admin"
            />
          )}

          <TopNav onClick={handleClickAway} />
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', paddingX: theme.spacing(1) }}>
          {navGroups.map(({ key, ...group }) => (
            <NavGroup key={key} {...group} onItemClick={handleClickAway} />
          ))}
        </Box>
      </Box>
      <Box
        height="55px"
        width="100%"
        sx={{
          backgroundColor: 'primary.main',
        }}
      >
        <Box sx={{ marginX: theme.spacing(1) }}>
          <UserButton />
        </Box>
      </Box>
    </Drawer>
  );
};
